import { Link, useParams } from "@remix-run/react";
import { FiAlertCircle, FiHome } from "react-icons/fi";

interface ErrorPageProps {
  status?: number;
  stack?: string;
  statusText?: string;
  message?: string;
}

export default function ErrorPage({
  status = 500,
  stack,
  statusText,
  message = "Sorry, something went wrong on our end.",
}: ErrorPageProps) {
  const params = useParams();
  // Friendly message based on status code
  const getFriendlyMessage = () => {
    if (status === 404) {
      return "We couldn't find the page you're looking for.";
    } else if (status >= 500) {
      return "We're experiencing some issues on our end. Our team has been notified.";
    } else {
      return message || "Something unexpected happened. Let's get you back on track.";
    }
  };

  // Choose icon based on error type
  const getErrorIcon = () => {
    if (status === 404) {
      return <FiAlertCircle className="h-24 w-24 text-amber-400" aria-hidden="true" />;
    } else {
      return <FiAlertCircle className="text-red-400 h-24 w-24" aria-hidden="true" />;
    }
  };

  console.error(`[${status}] ${statusText}: ${message}`, stack);
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white text-center shadow-lg">
        <div className="px-6 pb-6 pt-10">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-gray-100 p-6">{getErrorIcon()}</div>
          </div>

          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            Oops! {status === 404 ? "Page not found" : "Something went wrong"}
          </h1>

          <p className="mb-8 text-gray-600">{getFriendlyMessage()}</p>

          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              to={params.account ? `/${params.account}` : "/"}
              className="focus:ring-blue-500 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-3 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              <FiHome className="mr-2 h-5 w-5" />
              Go to home
            </Link>
          </div>
        </div>

        <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
          <p className="text-sm text-gray-500">If the problem persists, please contact support.</p>
        </div>
      </div>
    </div>
  );
}
